import { type BoxProps } from '@ironhack/design-system2/components/box';
import { pathEq } from 'ramda';
import { type ReactElement, type RefObject, useEffect } from 'react';
import { type SetRequired } from 'type-fest';

// Ref here for cloneElement to accept ref as a prop
export type ChildProps = SetRequired<BoxProps, 'title'> & {
  ref?: RefObject<HTMLDivElement>;
};

// Ref here to access ref from children arrays
export type Child = ReactElement<ChildProps> & {
  ref?: RefObject<HTMLDivElement>;
};

export type NavigationChild = SetRequired<Child, 'ref'>;

export const NAV_HEIGHT = 105;

// Extra margin to give sections when users click a navigation tab to scroll
export const CLICK_MARGIN = 4;

export const useScrollingIndexUpdate = (
  tabs: NavigationChild[],
  onIndexUpdate: (index: number) => void
): void => {
  // Effect for keeping track of the selected state as user scrolls
  useEffect(() => {
    // 2px gap between the threshold for normal scrolling and where the tab
    // buttons leave users when clicking tabs to scroll
    const rootMargin = NAV_HEIGHT + CLICK_MARGIN + 2;

    const observer = new IntersectionObserver(
      (entries) =>
        entries.forEach((entry) => {
          // We only want elements with negative top (hidden above the viewport)
          if (entry.boundingClientRect.top >= 0) return;

          let index = tabs.findIndex(pathEq(['ref', 'current'], entry.target));

          // Do nothing if this element has no navigation tab ref
          if (index === -1) return;

          // NOT intersecting means it's leaving the viewport (scrolling down).
          // Therefore the NEXT element's tab should be highlighted if exists.
          if (!entry.isIntersecting && tabs[index + 1]) {
            index += 1;
          }

          // Otherwise intersecting means entering the viewport (scrolling up).
          // So this element's tab should be highlighted.
          onIndexUpdate(index);
        }),
      { rootMargin: `-${rootMargin}px 0px 0px` }
    );

    tabs.forEach(({ ref }: NavigationChild): void => {
      if (ref.current) observer.observe(ref.current);
    });

    return () => observer.disconnect();
  }, [tabs, onIndexUpdate]);
};
